import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiError } from "../api/client";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import WorkshopTable from "../components/workshops/WorkshopTable";
import WorkshopMap from "../components/workshops/WorkshopMap";
import { formatDateFull } from "../utils/format";
import { Plus, Map as MapIcon, List, Loader2, Download } from "lucide-react";

const FILTERS = [
  { key: "future", label: "Budoucí" },
  { key: "all", label: "Všechny" },
  { key: "past", label: "Minulé" },
];

const LEGEND = [
  { c: "bg-orange-500", label: "Probíhá dnes" },
  { c: "bg-amber-400", label: "Do 4 dnů" },
  { c: "bg-emerald-500", label: "Realizováno" },
  { c: "bg-zinc-300", label: "Zrušeno" },
];

export default function WorkshopsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("future");
  const [view, setView] = useState("list");
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const canEdit = user?.role === "admin" || user?.role === "user";
  const statusLegend = Array.from(
    new Map(workshops.filter((w) => w.status_name).map((w) => [w.status_name, w.status_color || "#2563eb"])).entries()
  );

  const load = useCallback(() => {
    setLoading(true);
    client
      .get("/workshops", { params: { filter } })
      .then((res) => setWorkshops(res.data))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (w) => {
    if (!window.confirm(`Smazat workshop ${w.school_name} (${formatDateFull(w.date)})?`)) return;
    try {
      await client.delete(`/workshops/${w.id}`);
      toast.success("Workshop smazán");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const onNotify = async (w) => {
    if (!window.confirm(`Poslat připomínku e-mailem lektorům workshopu ${w.school_name} (${formatDateFull(w.date)})?`)) return;
    try {
      const res = await client.post(`/notifications/workshop/${w.id}`);
      toast.success(`Připomínka odeslána ${res.data.sent} z ${res.data.total} lektorům`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const res = await client.get("/workshops/export", { params: { filter }, responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename\*=UTF-8''([^;]+)/);
      const fname = match ? decodeURIComponent(match[1]) : `workshopy-${filter}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export do Excelu stažen");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setExporting(false);
    }
  };

  const onInvoice = async (w) => {
    if (w.invoice?.number) {
      if (!window.confirm("Odeslat fakturu škole e-mailem?")) return;
      try {
        await client.post(`/invoicing/workshops/${w.id}/invoice/send`);
        toast.success("Faktura odeslána");
      } catch (err) {
        toast.error(apiError(err));
      }
      return;
    }
    try {
      await client.post(`/invoicing/workshops/${w.id}/invoice`);
      toast.success("Faktura vytvořena");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <>
      <div className="h-16 border-b border-border px-8 flex items-center justify-between sticky top-0 bg-background/80 backdrop-blur-md z-20">
        <h1 className="font-heading text-xl font-semibold tracking-tight">Workshopy</h1>
        <div className="flex items-center gap-3">
          <div className="flex rounded-md border border-border overflow-hidden">
            <button
              data-testid="view-list"
              onClick={() => setView("list")}
              className={`h-9 px-3 text-sm flex items-center gap-1.5 ${view === "list" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            >
              <List className="h-4 w-4" /> Seznam
            </button>
            <button
              data-testid="view-map"
              onClick={() => setView("map")}
              className={`h-9 px-3 text-sm flex items-center gap-1.5 ${view === "map" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            >
              <MapIcon className="h-4 w-4" /> Mapa
            </button>
          </div>
          {canEdit && (
            <button
              data-testid="export-excel-btn"
              onClick={onExport}
              disabled={exporting}
              className="h-9 px-4 rounded-md border border-border text-sm font-medium hover:bg-secondary flex items-center gap-1.5 disabled:opacity-60"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export do Excelu
            </button>
          )}
          {canEdit && (
            <button
              data-testid="new-workshop-btn"
              onClick={() => navigate("/workshop/new")}
              className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 flex items-center gap-1.5"
            >
              <Plus className="h-4 w-4" /> Nový workshop
            </button>
          )}
        </div>
      </div>

      <div className="px-8 py-4 flex items-center justify-between flex-wrap gap-3 border-b border-border">
        <div className="flex rounded-md border border-border overflow-hidden">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`h-8 px-4 text-sm ${filter === f.key ? "bg-secondary font-medium" : "hover:bg-secondary/50 text-muted-foreground"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {LEGEND.map((l) => (
            <span key={l.label} className="flex items-center gap-1.5">
              <span className={`h-2.5 w-2.5 rounded-full ${l.c}`} /> {l.label}
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Načítání…
        </div>
      ) : view === "list" ? (
        <div className="p-8">
          <WorkshopTable
            workshops={workshops}
            canEdit={canEdit}
            onDelete={onDelete}
            onNotify={canEdit ? onNotify : null}
            onInvoice={canEdit ? onInvoice : null}
          />
        </div>
      ) : (
        <div className="flex" style={{ height: "calc(100vh - 8rem)" }}>
          <div className="w-[30%] border-r border-border overflow-y-auto" data-testid="map-list">
            {statusLegend.length > 0 && (
              <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border px-4 py-2 flex flex-wrap gap-x-3 gap-y-1 z-10" data-testid="map-legend">
                {statusLegend.map(([name, color]) => (
                  <span key={name} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} /> {name}
                  </span>
                ))}
              </div>
            )}
            {workshops.map((w) => (
              <button
                key={w.id}
                data-testid={`map-list-item-${w.id}`}
                onClick={() => setSelectedId(w.id)}
                className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary/50 transition-colors ${selectedId === w.id ? "bg-secondary" : ""}`}
              >
                <div className="font-medium text-sm truncate">{w.school_name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatDateFull(w.date)} · {w.time_from}
                </div>
                <div className="text-xs text-muted-foreground truncate">{w.address}</div>
              </button>
            ))}
            {workshops.length === 0 && <div className="p-6 text-sm text-muted-foreground">Žádné workshopy</div>}
          </div>
          <div className="w-[70%]">
            <WorkshopMap workshops={workshops} selectedId={selectedId} onSelect={setSelectedId} onEdit={(id) => navigate(`/workshop/edit/${id}`)} />
          </div>
        </div>
      )}
    </>
  );
}
